import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import { useApiToast } from '../../hooks/useApiToast';
import useTrendingTokens from '../../hooks/useTrendingTokens';
import QuickTradeModal from './QuickTradeModal';
import BotConfigModal from './BotConfigModal';
import MyTokensTab from './MyTokensTab';
import InvestmentTab from './InvestmentTab';
import PriceAlertModal from '../alerts/PriceAlertModal';
import PriceTargetModal from './PriceTargetModal';
import PriceTargetsList from './PriceTargetsList';
import OpportunityIndicators from './OpportunityIndicators';
import OpportunityActions from './OpportunityActions';
import TrendingIndicators from './TrendingIndicators';
import PublicAnalytics from '../analytics/PublicAnalytics';
import opportunityService from '../../services/opportunityService';

const TokenBrowser = ({ initialTab = 'trending' }) => {
  const { tokens } = useAuth();
  const { wallets, activeWallet } = useWallet();
  const { handleTokenOperation } = useApiToast();
  
  // Trending tokens hook
  const {
    trendingTokens,
    loading: trendingLoading,
    error: trendingError,
    lastUpdated: trendingLastUpdated,
    refresh: refreshTrending,
    isTokenTrending,
    getTrendingData
  } = useTrendingTokens({
    autoRefresh: true,
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    limit: 50,
    enabled: true
  });
  
  // Existing state
  const [discoveredTokens, setDiscoveredTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedSource, setSelectedSource] = useState('all');
  const [addingToWatchlist, setAddingToWatchlist] = useState(new Set());
  
  // New state for trading enhancements
  const [myTokens, setMyTokens] = useState([]);
  const [activePositions, setActivePositions] = useState([]);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [tradingOpportunities, setTradingOpportunities] = useState([]);
  const [publicAnalytics, setPublicAnalytics] = useState({});
  
  // Modal states
  const [quickTradeModal, setQuickTradeModal] = useState({ isOpen: false, token: null, type: 'buy' });
  const [botConfigModal, setBotConfigModal] = useState({ isOpen: false, token: null });
  const [alertModal, setAlertModal] = useState({ isOpen: false, token: null });
  const [priceTargetModal, setPriceTargetModal] = useState({ isOpen: false, token: null, type: 'buy' });
  
  // Real-time data
  const [portfolioSummary, setPortfolioSummary] = useState({
    totalValue: 0,
    totalPnL: 0,
    pnL24h: 0,
    activeBots: 0,
    activeAlerts: 0,
    topPerformer: null,
    worstPerformer: null
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';

  // Enhanced tab structure
  const tabs = [
    { id: 'trending', label: 'Trending', icon: '🔥' },
    { id: 'search', label: 'Search', icon: '🔍' },
    { id: 'my-tokens', label: 'My Tokens', icon: '💼' },
    { id: 'investment', label: 'Investment Pool', icon: '🏦' },
    { id: 'opportunities', label: 'Opportunities', icon: '📈' },
    { id: 'alerts', label: 'Alerts', icon: '🔔' },
    { id: 'price-targets', label: 'Price Targets', icon: '🎯' },
    { id: 'analytics', label: 'Public Analytics', icon: '📊' }
  ];

  useEffect(() => {
    if (tokens?.access_token) {
      fetchTrendingTokens();
      fetchPortfolioData();
      fetchAlertsData();
    }
  }, [tokens, selectedSource]);

  // Fetch portfolio data when switching to My Tokens tab
  useEffect(() => {
    if (activeTab === 'my-tokens' && tokens?.access_token) {
      fetchMyTokens();
    }
  }, [activeTab, tokens]);

  // Investment tab is handled by InvestmentTab component
  // No need to fetch investment data here

  // Fetch opportunities when switching to Opportunities tab
  useEffect(() => {
    if (activeTab === 'opportunities' && tokens?.access_token) {
      fetchTradingOpportunities();
    }
  }, [activeTab, tokens]);



  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchTrendingTokens = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: '50'
      });

      if (selectedSource !== 'all') {
        params.append('source', selectedSource);
      }

      const data = await makeAuthenticatedRequest(`/tokens/trending?${params}`);
      setDiscoveredTokens(data.data.tokens || []);
    } catch (error) {
      setError(error.message);
      console.error('Error fetching trending tokens:', error);
    } finally {
      setLoading(false);
    }
  };

  // New data fetching functions
  const fetchPortfolioData = async () => {
    try {
      const data = await makeAuthenticatedRequest('/portfolio/summary');
      if (data.success) {
        setPortfolioSummary(data.data.summary);
      }
    } catch (error) {
      console.warn('Could not fetch portfolio data:', error.message);
    }
  };

  const fetchMyTokens = async () => {
    try {
      setLoading(true);
      const data = await makeAuthenticatedRequest('/portfolio/positions');
      if (data.success) {
        setMyTokens(data.data.positions || []);
        setActivePositions(data.data.positions?.filter(p => p.botStatus) || []);
      }
    } catch (error) {
      setError(error.message);
      console.error('Error fetching my tokens:', error);
    } finally {
      setLoading(false);
    }
  };

  // Investment data fetching is handled by InvestmentTab component

  const fetchAlertsData = async () => {
    try {
      const data = await makeAuthenticatedRequest('/alerts/active');
      if (data.success) {
        setPriceAlerts(data.data.alerts || []);
      }
    } catch (error) {
      console.warn('Could not fetch alerts data:', error.message);
    }
  };

  const fetchTradingOpportunities = async () => {
    try {
      setLoading(true);

      // Always use the public active opportunities endpoint for the opportunities tab
      const data = await opportunityService.getActiveOpportunities();

      // Handle both wrapped and unwrapped responses
      if (data.success && data.data) {
        setTradingOpportunities(data.data.opportunities || []);
      } else if (data.opportunities) {
        // Handle direct data response (apiClient stripped wrapper)
        setTradingOpportunities(data.opportunities || []);
      } else if (Array.isArray(data)) {
        // Handle direct array response
        setTradingOpportunities(data);
      } else {
        setTradingOpportunities([]);
      }
    } catch (error) {
      setError(error.message);
      console.error('Error fetching trading opportunities:', error);
      setTradingOpportunities([]);
    } finally {
      setLoading(false);
    }
  };



  const searchTokens = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      setError(null);

      const params = new URLSearchParams({
        q: query,
        limit: '20'
      });

      if (selectedSource !== 'all') {
        params.append('source', selectedSource);
      }

      const data = await makeAuthenticatedRequest(`/tokens/search?${params}`);
      setSearchResults(data.data.tokens || []);
    } catch (error) {
      setError(error.message);
      console.error('Error searching tokens:', error);
    } finally {
      setIsSearching(false);
    }
  }, [tokens, selectedSource]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (activeTab === 'search') {
        searchTokens(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, activeTab, searchTokens]);

  const addToWatchlist = async (token) => {
    try {
      setAddingToWatchlist(prev => new Set(prev).add(token.address));

      await handleTokenOperation(
        () => makeAuthenticatedRequest('/tokens/watchlist', {
          method: 'POST',
          body: JSON.stringify({
            token_address: token.address,
            notes: `Added from ${token.source}`
          })
        }),
        {
          operationType: 'add',
          tokenSymbol: token.symbol,
          successMessage: `${token.symbol} added to watchlist!`
        }
      );
    } catch (error) {
      // Error is already handled by handleTokenOperation
    } finally {
      setAddingToWatchlist(prev => {
        const newSet = new Set(prev);
        newSet.delete(token.address);
        return newSet;
      });
    }
  };

  // Modal handler functions
  const openQuickTrade = (token, type = 'buy') => {
    setQuickTradeModal({ isOpen: true, token, type });
  };

  const closeQuickTrade = () => {
    setQuickTradeModal({ isOpen: false, token: null, type: 'buy' });
    // Refresh portfolio data after trade
    fetchPortfolioData();
    if (activeTab === 'my-tokens') {
      fetchMyTokens();
    }
  };

  const openBotConfig = (token, existingPosition = null) => {
    setBotConfigModal({ isOpen: true, token, existingPosition });
  };

  const closeBotConfig = () => {
    setBotConfigModal({ isOpen: false, token: null, existingPosition: null });
    // Refresh portfolio data after bot configuration
    fetchPortfolioData();
    if (activeTab === 'my-tokens') {
      fetchMyTokens();
    }
  };

  const openAlertModal = (token) => {
    setAlertModal({ isOpen: true, token });
  };

  const closeAlertModal = () => {
    setAlertModal({ isOpen: false, token: null });
    // Refresh alerts data
    fetchAlertsData();
  };

  const openPriceTargetModal = (token, type = 'buy') => {
    setPriceTargetModal({ isOpen: true, token, type });
  };

  const closePriceTargetModal = () => {
    setPriceTargetModal({ isOpen: false, token: null, type: 'buy' });
  };

  // Investment modals are handled by InvestmentTab component

  // Helper function to check if user holds a token
  const userHoldsToken = (token) => {
    return myTokens.some(myToken => myToken.address === token.address && myToken.balance > 0);
  };

  // Helper function to get user's token balance
  const getUserTokenBalance = (tokenAddress) => {
    const userToken = myTokens.find(token => token.address === tokenAddress);
    return userToken ? userToken.balance : 0;
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';

    // Handle string values from database
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;

    if (isNaN(numPrice)) return 'N/A';
    if (numPrice === 0) return '$0.00';
    if (numPrice < 0.000001) return `$${numPrice.toExponential(2)}`;
    if (numPrice < 0.01) return `$${numPrice.toFixed(6)}`;
    if (numPrice < 1) return `$${numPrice.toFixed(4)}`;
    return `$${numPrice.toFixed(2)}`;
  };

  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toFixed(2);
  };

  const formatPercentage = (percent) => {
    if (!percent || percent === 0) return '0.00%';

    // Handle string values from database
    const numPercent = typeof percent === 'string' ? parseFloat(percent) : percent;

    if (isNaN(numPercent)) return '0.00%';

    const formatted = numPercent.toFixed(2);
    return `${numPercent >= 0 ? '+' : ''}${formatted}%`;
  };

  const getSourceBadgeColor = (source) => {
    switch (source) {
      case 'dexscreener': return '#007bff';
      case 'pumpfun': return '#ff6b35';
      case 'letsbunk': return '#6f42c1';
      default: return '#6c757d';
    }
  };

  const renderTokenCard = (token) => {
    // Get opportunities for this token
    const tokenOpportunities = tradingOpportunities.filter(opp => opp.token_address === token.address);
    
    // Get trending data for this token
    const trendingData = getTrendingData(token.address);
    const isTrending = isTokenTrending(token.address);
    
    return (
      <div key={token.address} className={`token-card enhanced ${isTrending ? 'trending' : ''}`}>
        <div className="token-header">
          <div className="token-info">
            {token.imageUrl && (
              <img 
                src={token.imageUrl} 
                alt={token.symbol}
                className="token-image"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            <div className="token-details">
              <div className="token-name-row">
                <span className="token-symbol">{token.symbol}</span>
                <span 
                  className="source-badge"
                  style={{ backgroundColor: getSourceBadgeColor(token.source) }}
                >
                  {token.source}
                </span>
                {/* Add trending indicators */}
                {isTrending && (
                  <TrendingIndicators 
                    token={token}
                    trendingData={trendingData}
                    size="small"
                  />
                )}
              </div>
              <span className="token-name">{token.name}</span>
              {token.address && (
                <span className="token-address">
                  {token.address.slice(0, 6)}...{token.address.slice(-4)}
                </span>
              )}
            </div>
          </div>
          
          <div className="token-price">
            <span className="price-value">{formatPrice(token.price)}</span>
            {token.priceChange24h && token.priceChange24h !== 0 && (
              <span className={`price-change ${token.priceChange24h >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(token.priceChange24h)}
              </span>
            )}
          </div>
        </div>

        {/* Enhanced opportunity indicators */}
        {tokenOpportunities.length > 0 && (
          <OpportunityIndicators 
            opportunities={tokenOpportunities}
            className="compact"
          />
        )}

        <div className="token-metrics">
          <div className="metric">
            <span className="metric-label">Volume 24h</span>
            <span className="metric-value">${formatNumber(token.volume24h || 0)}</span>
          </div>
          <div className="metric">
            <span className="metric-label">Market Cap</span>
            <span className="metric-value">${formatNumber(token.marketCap || 0)}</span>
          </div>
          <div className="metric">
            <span className="metric-label">Liquidity</span>
            <span className="metric-value">${formatNumber(token.liquidity || 0)}</span>
          </div>
        </div>

        {token.description && (
          <div className="token-description">
            <p>{token.description.slice(0, 100)}{token.description.length > 100 ? '...' : ''}</p>
          </div>
        )}

        {/* Opportunity-based quick actions */}
        {tokenOpportunities.length > 0 && (
          <OpportunityActions
            token={token}
            opportunities={tokenOpportunities}
            onQuickBuy={(token) => openQuickTrade(token, 'buy')}
            onQuickSell={(token) => openQuickTrade(token, 'sell')}
            onAddToBot={(token) => openBotConfig(token)}
            onSetAlert={(token) => openAlertModal(token)}
            userBalance={getUserTokenBalance(token.address)}
            className="compact"
          />
        )}

        {/* Enhanced action buttons */}
        <div className="token-actions enhanced">
          <div className="primary-actions">
            <button
              className="quick-buy-btn"
              onClick={() => openQuickTrade(token, 'buy')}
              disabled={!activeWallet}
            >
              Quick Buy
            </button>
            
            {userHoldsToken(token) && (
              <button
                className="quick-sell-btn"
                onClick={() => openQuickTrade(token, 'sell')}
              >
                Quick Sell
              </button>
            )}
            
            <button
              className="add-to-bot-btn"
              onClick={() => openBotConfig(token)}
              disabled={!activeWallet}
            >
              Add to Bot
            </button>
          </div>
          
          <div className="secondary-actions">
            <button
              className="set-alert-btn"
              onClick={() => openAlertModal(token)}
            >
              Set Alert
            </button>

            <button
              className="price-target-btn"
              onClick={() => openPriceTargetModal(token, 'buy')}
            >
              Price Target
            </button>
            
            <button
              className="add-to-watchlist-btn"
              onClick={() => addToWatchlist(token)}
              disabled={addingToWatchlist.has(token.address)}
            >
              {addingToWatchlist.has(token.address) ? 'Adding...' : 'Watchlist'}
            </button>
            
            {token.url && (
              <a 
                href={token.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="view-details-btn"
              >
                Details
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  const currentTokens = activeTab === 'search' ? searchResults : discoveredTokens;

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'trending':
        return renderTrendingTab();
      case 'search':
        return renderSearchTab();
      case 'my-tokens':
        return renderMyTokensTab();
      case 'investment':
        return renderInvestmentTab();
      case 'opportunities':
        return renderOpportunitiesTab();
      case 'alerts':
        return renderAlertsTab();
      case 'price-targets':
        return renderPriceTargetsTab();
      case 'analytics':
        return renderAnalyticsTab();
      default:
        return renderTrendingTab();
    }
  };

  const renderTrendingTab = () => (
    <div className="trending-tab">
      {/* Trending Status Header */}
      <div className="trending-header">
        <div className="trending-stats">
          <div className="stat-item">
            <span className="stat-label">Trending Tokens:</span>
            <span className="stat-value">{trendingTokens.length}</span>
          </div>
          {trendingLastUpdated && (
            <div className="stat-item">
              <span className="stat-label">Last Updated:</span>
              <span className="stat-value">
                {new Date(trendingLastUpdated).toLocaleTimeString()}
              </span>
            </div>
          )}
          <button 
            onClick={refreshTrending}
            className="refresh-btn"
            disabled={trendingLoading}
          >
            {trendingLoading ? '🔄' : '↻'} Refresh
          </button>
        </div>
      </div>

      {/* Source Filter */}
      <div className="filter-section">
        <label>Source:</label>
        <select 
          value={selectedSource} 
          onChange={(e) => setSelectedSource(e.target.value)}
          className="source-filter"
        >
          <option value="all">All Sources</option>
          <option value="dexscreener">DexScreener</option>
          <option value="pumpfun">Pump.fun</option>
          <option value="on_chain">On-Chain</option>
        </select>
      </div>

      {/* Trending Tokens Grid */}
      {trendingLoading && trendingTokens.length === 0 ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Scanning for trending tokens...</p>
        </div>
      ) : trendingError ? (
        <div className="error-state">
          <p>Error loading trending tokens: {trendingError}</p>
          <button onClick={refreshTrending} className="retry-btn">
            Try Again
          </button>
        </div>
      ) : (
        renderTokenGrid(
          trendingTokens.map(trending => ({
            address: trending.token_address,
            symbol: trending.symbol,
            name: trending.name,
            source: trending.detection_source,
            price: null, // Price not available for trending tokens from detection
            trending: true,
            trendingData: trending
          })), 
          'No trending tokens found', 
          'Check back in a few minutes for new trending tokens'
        )
      )}
    </div>
  );

  const renderSearchTab = () => (
    <div className="search-tab">
      {/* Search Bar */}
      <div className="search-section">
        <div className="search-input-container">
          <input
            type="text"
            placeholder="Search tokens by name, symbol, or address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {isSearching && <div className="search-spinner"></div>}
        </div>
      </div>

      {renderTokenGrid(
        searchResults, 
        searchQuery ? 'No tokens found' : 'Start searching for tokens',
        searchQuery ? 'Try searching with different keywords' : 'Enter a token name, symbol, or address to search'
      )}
    </div>
  );

  const renderMyTokensTab = () => (
    <MyTokensTab
      onQuickSell={(token) => openQuickTrade(token, 'sell')}
      onAddToBot={(token, existingPosition) => openBotConfig(token, existingPosition)}
      onSetAlert={(token) => openAlertModal(token)}
      portfolioSummary={portfolioSummary}
      refreshTrigger={Date.now()} // Force refresh when needed
    />
  );

  const renderInvestmentTab = () => (
    <InvestmentTab />
  );

  const renderOpportunitiesTab = () => (
    <div className="opportunities-tab">
      <div className="opportunities-header">
        <h3>Trading Opportunities</h3>
        <p>Discover tokens with high potential based on market analysis</p>
        <div className="opportunities-stats">
          <span className="stat-item">Active Opportunities: {tradingOpportunities.length}</span>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Scanning for opportunities...</p>
        </div>
      ) : tradingOpportunities.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📈</div>
          <h3>No opportunities found</h3>
          <p>Check back later for new trading opportunities</p>
        </div>
      ) : (
        <div className="opportunities-list">
          {tradingOpportunities.map(opportunity => (
            <div key={opportunity.id || opportunity.token_address} className="opportunity-card">
              <div className="opportunity-header">
                <div className="token-info">
                  {opportunity.logo_uri && (
                    <img
                      src={opportunity.logo_uri}
                      alt={opportunity.symbol || 'Token'}
                      className="token-image"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <div className="token-details">
                    <span className="token-symbol">{opportunity.symbol || 'Unknown'}</span>
                    <span className="token-name">{opportunity.name || 'Unknown Token'}</span>
                    <span className="token-address">
                      {opportunity.token_address ?
                        `${opportunity.token_address.slice(0, 6)}...${opportunity.token_address.slice(-4)}` :
                        'No address'
                      }
                    </span>
                  </div>
                </div>
                <div className="opportunity-confidence">
                  <span className="confidence-score">
                    {opportunity.confidence_score !== undefined && opportunity.confidence_score !== null ?
                      `${(parseFloat(opportunity.confidence_score) * 100).toFixed(0)}%` :
                      'N/A'
                    }
                  </span>
                  <span className="confidence-label">Confidence</span>
                </div>
              </div>

              <OpportunityIndicators
                opportunities={[opportunity]}
              />

              <div className="opportunity-details">
                <p>{opportunity.description || 'No description available'}</p>
                <div className="opportunity-metrics">
                  <div className="metric">
                    <span className="label">Type</span>
                    <span className="value">{opportunity.opportunity_type || 'Unknown'}</span>
                  </div>
                  {opportunity.price_change_1h !== undefined && (
                    <div className="metric">
                      <span className="label">Price Change (1h)</span>
                      <span className={`value ${opportunity.price_change_1h >= 0 ? 'positive' : 'negative'}`}>
                        {formatPercentage(opportunity.price_change_1h)}
                      </span>
                    </div>
                  )}
                  {opportunity.volume_change_24h !== undefined && (
                    <div className="metric">
                      <span className="label">Volume Change (24h)</span>
                      <span className="value">
                        {formatPercentage(opportunity.volume_change_24h)}
                      </span>
                    </div>
                  )}
                  {opportunity.trigger_price && (
                    <div className="metric">
                      <span className="label">Trigger Price</span>
                      <span className="value">
                        {formatPrice(opportunity.trigger_price)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <OpportunityActions
                token={{
                  address: opportunity.token_address,
                  symbol: opportunity.symbol || 'Unknown',
                  name: opportunity.name || 'Unknown Token',
                  price: opportunity.trigger_price || 0
                }}
                opportunities={[opportunity]}
                onQuickBuy={(token) => openQuickTrade(token, 'buy')}
                onQuickSell={(token) => openQuickTrade(token, 'sell')}
                onAddToBot={(token) => openBotConfig(token)}
                onSetAlert={(token) => openAlertModal(token)}
                userBalance={getUserTokenBalance(opportunity.token_address)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderAlertsTab = () => (
    <div className="alerts-tab">
      <div className="alerts-header">
        <h3>Price Alerts</h3>
        <p>Manage your active price alerts</p>
      </div>
      
      {priceAlerts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔔</div>
          <h3>No active alerts</h3>
          <p>Set price alerts from token cards to get notified of price movements</p>
        </div>
      ) : (
        <div className="alerts-list">
          {priceAlerts.map(alert => (
            <div key={alert.id} className="alert-card">
              <div className="alert-info">
                <span className="token-symbol">{alert.tokenSymbol}</span>
                <span className="alert-condition">
                  {alert.alertType === 'price_above' ? '📈' : '📉'} 
                  {alert.alertType === 'price_above' ? 'Above' : 'Below'} ${alert.targetPrice}
                </span>
              </div>
              <button className="remove-alert-btn">Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPriceTargetsTab = () => (
    <div className="price-targets-tab">
      <PriceTargetsList showCreateButton={true} />
    </div>
  );

  const renderAnalyticsTab = () => (
    <div className="analytics-tab">
      <PublicAnalytics />
      )
    </div>
  );

  const renderTokenGrid = (tokens, emptyTitle, emptyDescription) => (
    <>
      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          {error}
          <button className="error-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading tokens...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && tokens.length === 0 && !error && (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3>{emptyTitle}</h3>
          <p>{emptyDescription}</p>
        </div>
      )}

      {/* Token Grid */}
      {!loading && tokens.length > 0 && (
        <div className="tokens-grid">
          {tokens.map(renderTokenCard)}
        </div>
      )}
    </>
  );

  return (
    <div className="token-browser">
      <div className="browser-header">
        <h2>Token Discovery</h2>
        <p>Discover trending tokens from multiple sources</p>
      </div>

      {/* Portfolio Summary Header */}
      {tokens?.access_token && (
        <div className="portfolio-summary-header">
          <div className="summary-metrics">
            <div className="metric">
              <span className="metric-label">Portfolio Value</span>
              <span className="metric-value">${(portfolioSummary.totalValue || 0).toFixed(2)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">24h P&L</span>
              <span className={`metric-value ${(portfolioSummary.pnL24h || 0) >= 0 ? 'positive' : 'negative'}`}>
                {(portfolioSummary.pnL24h || 0) >= 0 ? '+' : ''}{(portfolioSummary.pnL24h || 0).toFixed(2)}%
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Active Bots</span>
              <span className="metric-value">{portfolioSummary.activeBots || 0}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Active Alerts</span>
              <span className="metric-value">{portfolioSummary.activeAlerts || 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Tab Navigation */}
      <div className="tab-navigation enhanced">
        {tabs.map(tab => (
          <button 
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              // Handle special tab logic
              if (tab.id === 'trending') {
                setSelectedSource('all');
              }
            }}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>

      {/* Modals */}
      {quickTradeModal.isOpen && (
        <QuickTradeModal
          isOpen={quickTradeModal.isOpen}
          onClose={closeQuickTrade}
          token={quickTradeModal.token}
          tradeType={quickTradeModal.type}
          userTokenBalance={getUserTokenBalance(quickTradeModal.token?.address)}
        />
      )}

      {botConfigModal.isOpen && (
        <BotConfigModal
          isOpen={botConfigModal.isOpen}
          onClose={closeBotConfig}
          token={botConfigModal.token}
          existingPosition={botConfigModal.existingPosition}
        />
      )}

      {alertModal.isOpen && (
        <PriceAlertModal
          isOpen={alertModal.isOpen}
          onClose={closeAlertModal}
          token={alertModal.token}
        />
      )}

      {priceTargetModal.isOpen && (
        <PriceTargetModal
          isOpen={priceTargetModal.isOpen}
          onClose={closePriceTargetModal}
          token={priceTargetModal.token}
          initialType={priceTargetModal.type}
        />
      )}

    </div>
  );
};





export default TokenBrowser;